/**
 * What an app grant lets a person do — the frontend mirror of
 * backend/auth/middlewares/grants.js (keep the two in step:
 * backend/shared/tests/app-grants-mirror.test.js compares them).
 *
 * A grant in `allowed_apps` is an app code ("gl") or the same code with ":view"
 * ("gl:view", 2026-11-04). The role says what a person may do inside the apps
 * they hold (admin = change, user = look); a view-only grant narrows ONE app to
 * looking, whatever the role. Pure: no Vue, no I/O.
 */

/** Suffix that makes a grant view-only: "gl:view". */
export const VIEW_SUFFIX = ":view";

const ADMIN_ROLES = new Set(["admin", "appadmin"]);

/**
 * The grants held, split by kind. A code held both ways counts as full.
 * @param {string[]|string|null|undefined} allowedApps
 * @returns {{ full: Set<string>, view: Set<string> }}
 */
export function parseGrants(allowedApps) {
  let list = allowedApps || [];
  if (typeof list === "string") {
    try { list = JSON.parse(list); } catch { list = []; }
  }
  if (!Array.isArray(list)) list = [];
  const full = new Set();
  const view = new Set();
  for (const raw of list) {
    if (typeof raw !== "string" || !raw) continue;
    if (raw.endsWith(VIEW_SUFFIX)) {
      const code = raw.slice(0, -VIEW_SUFFIX.length);
      if (code) view.add(code);
    } else {
      full.add(raw);
    }
  }
  for (const code of full) view.delete(code);
  return { full, view };
}

/**
 * Does the user hold one of `codes`? A view-only grant counts only when
 * reading. An appadmin holds every app.
 * @param {{role?: string, allowed_apps?: any}|null|undefined} user
 * @param {string|string[]} codes
 * @param {{ reading?: boolean }} [opts] reading defaults to true
 */
export function holdsApp(user, codes, { reading = true } = {}) {
  if (!user) return false;
  if (user.role === "appadmin") return true;
  const wanted = Array.isArray(codes) ? codes : [codes];
  const { full, view } = parseGrants(user.allowed_apps);
  return wanted.some((c) => full.has(c) || (reading && view.has(c)));
}

/** May the user CHANGE data in one of these apps? An admin-tier role and a full grant. */
export function canEditApp(user, codes) {
  if (!user || !ADMIN_ROLES.has(user.role)) return false;
  return holdsApp(user, codes, { reading: false });
}

/**
 * May the user see salaries? The payroll grant (to change or only to look), or
 * an admin who keeps the books (full accounting / gl) — backend
 * lib/payroll-access.js canReadPayroll().
 */
export function canReadPayroll(user) {
  return holdsApp(user, ["hr-payroll"]) || canEditApp(user, ["accounting", "gl"]);
}

/** A grant list as it should be stored: trimmed, no duplicates, no ":view" twin of a full grant. */
export function normalizeGrants(list) {
  if (!Array.isArray(list)) return [];
  const clean = list.filter((g) => typeof g === "string").map((g) => g.trim()).filter(Boolean);
  const full = new Set(clean.filter((g) => !g.endsWith(VIEW_SUFFIX)));
  const seen = new Set();
  const out = [];
  for (const g of clean) {
    const code = g.endsWith(VIEW_SUFFIX) ? g.slice(0, -VIEW_SUFFIX.length) : g;
    if (!code) continue;
    if (g.endsWith(VIEW_SUFFIX) && full.has(code)) continue;
    if (seen.has(g)) continue;
    seen.add(g);
    out.push(g);
  }
  return out;
}

/** The app code of a grant, without ":view". */
export function grantCode(grant) {
  return typeof grant === "string" && grant.endsWith(VIEW_SUFFIX) ? grant.slice(0, -VIEW_SUFFIX.length) : grant;
}
